import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, ArchiveRestore, Paperclip, X } from "lucide-react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { CaseRow } from "./LegalCasesList";
import { EmpSelect } from "./LegalPickers";
import { FILE_CATS } from "./LegalFiles";
import { errMsg } from "./legalUtils";

const AddArchive = ({ meta, employees, onClose, onDone }) => {
  const [f, setF] = useState({ title: "", employee_id: "", category: meta.categories[0], result: "", notes: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inp = useRef(null);
  const save = async () => {
    if (!f.title.trim()) { toast.error("اكتب عنوان القضية"); return; }
    setBusy(true);
    try {
      const attachments = [];
      for (const x of files) { const r = await api.uploadFile(x.file); attachments.push({ file_id: r.attachment_id, name: r.attachment_name || x.file.name, category: x.cat }); }
      await api.legalArchiveAdd({ ...f, attachments }); toast.success("أُضيفت للأرشيف"); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="legal-archive-dialog">
        <DialogHeader className="text-right"><DialogTitle className="font-heading">إضافة قضية للأرشيف</DialogTitle><DialogDescription>لأرشفة قضايا ومستندات سابقة وتصنيفها</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="عنوان القضية" data-testid="legal-archive-title" />
          <EmpSelect employees={employees} value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} testId="legal-archive-emp" />
          <div className="grid grid-cols-2 gap-2">
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="h-9 rounded-md border px-2 text-sm" data-testid="legal-archive-category">{meta.categories.map((c) => <option key={c}>{c}</option>)}</select>
            <select value={f.result} onChange={(e) => setF({ ...f, result: e.target.value })} className="h-9 rounded-md border px-2 text-sm"><option value="">— النتيجة —</option>{meta.results.map((c) => <option key={c}>{c}</option>)}</select>
          </div>
          <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3} placeholder="ملخص / ملاحظات" />
          <input ref={inp} type="file" multiple className="hidden" onChange={(e) => { setFiles((p) => [...p, ...[...e.target.files].map((file) => ({ file, cat: FILE_CATS[0] }))]); e.target.value = ""; }} data-testid="legal-archive-files" />
          {files.map((x, i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg bg-slate-50 px-2 py-1 text-xs">
              <span className="flex-1 truncate">{x.file.name}</span>
              <select value={x.cat} onChange={(e) => setFiles((p) => p.map((y, j) => (j === i ? { ...y, cat: e.target.value } : y)))} className="h-7 rounded border px-1">{FILE_CATS.map((c) => <option key={c}>{c}</option>)}</select>
              <button onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}><X className="h-3.5 w-3.5" /></button>
            </div>))}
          <Button variant="outline" onClick={() => inp.current?.click()} className="w-full gap-1.5"><Paperclip className="h-4 w-4" /> إضافة ملفات ومستندات</Button>
          <Button onClick={save} disabled={busy} className="w-full bg-[#0f1f3d]" data-testid="legal-archive-save">{busy && <Loader2 className="me-1 h-4 w-4 animate-spin" />}حفظ في الأرشيف</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const LegalArchive = ({ meta, employees, tick, onOpen, onChanged }) => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [add, setAdd] = useState(false);
  useEffect(() => { const t = setTimeout(() => api.legalCases("archive", q).then((r) => setRows(r.items)).catch(() => setRows([])), q ? 300 : 0); return () => clearTimeout(t); }, [q, tick]);
  const shown = (rows || []).filter((r) => !cat || r.category === cat);
  const count = (c) => (rows || []).filter((r) => r.category === c).length;
  return (
    <div className="space-y-3" data-testid="legal-archive">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative"><Search className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث في الأرشيف والمستندات" className="h-9 w-72 pr-8" data-testid="legal-archive-search" /></div>
        {meta.has_access && <Button onClick={() => setAdd(true)} className="ms-auto gap-1.5 bg-[#0f1f3d]" data-testid="legal-archive-add"><ArchiveRestore className="h-4 w-4" /> إضافة للأرشيف</Button>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => setCat("")} className={`rounded-full px-3 py-1 text-xs font-bold ${!cat ? "bg-[#0f1f3d] text-white" : "bg-slate-100"}`} data-testid="legal-archive-cat-all">الكل ({rows?.length || 0})</button>
        {meta.categories.map((c) => <button key={c} onClick={() => setCat(c)} data-testid={`legal-archive-cat-${c}`} className={`rounded-full px-3 py-1 text-xs font-bold ${cat === c ? "bg-amber-400 text-[#0f1f3d]" : "bg-slate-100 text-slate-600"}`}>{c} ({count(c)})</button>)}
      </div>
      {rows === null ? <div className="grid place-items-center py-14"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        : shown.length === 0 ? <p className="rounded-2xl border border-dashed py-14 text-center text-sm text-slate-400">الأرشيف فارغ</p>
          : <div className="space-y-2">{shown.map((c) => <CaseRow key={c.id} c={c} onOpen={onOpen} />)}</div>}
      {add && <AddArchive meta={meta} employees={employees} onClose={() => setAdd(false)} onDone={() => { setAdd(false); onChanged(); }} />}
    </div>
  );
};
